import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Upload,
  Space,
  Typography,
  Row,
  Col,
  message,
  Divider,
} from 'antd';
import {
  UserAddOutlined,
  UploadOutlined,
  CameraOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../utils/api';

const { Title, Text } = Typography;

const StyledCard = styled(Card)`
  border-radius: 16px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  max-width: 1200px;
  margin: 0 auto;
`;

const ImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-top: 16px;
`;

const ImagePreviewCard = styled.div`
  position: relative;
  aspect-ratio: 1;
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #fafafa;
  cursor: ${props => props.isEmpty ? 'pointer' : 'default'};
  transition: all 0.3s;

  &:hover {
    border-color: ${props => props.isEmpty ? '#B4A0F7' : '#d9d9d9'};
    background: ${props => props.isEmpty ? '#f5f3ff' : '#fafafa'};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:hover .delete-overlay {
    opacity: 1;
  }
`;

const AddPhotoPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8c8c8c;
  font-size: 14px;
  gap: 8px;

  .anticon {
    font-size: 32px;
    color: #B4A0F7;
  }
`;

const DeleteOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
  cursor: pointer;
  color: white;
  font-size: 16px;
`;

const StyledInput = styled(Input)`
  border-radius: 12px !important;
  border: 2px solid #e8e8e8 !important;
  padding: 8px 16px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 48px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
  font-size: 15px !important;

  &:hover {
    border-color: #B4A0F7 !important;
    box-shadow: 0 4px 12px rgba(180, 160, 247, 0.15) !important;
  }

  &:focus,
  &.ant-input-focused {
    border-color: #B4A0F7 !important;
    box-shadow: 0 4px 16px rgba(180, 160, 247, 0.25) !important;
  }

  &::placeholder {
    color: #a0a0a0;
    font-weight: 400;
  }

  .ant-input-prefix {
    color: #8c8c8c;
    margin-right: 12px;
  }
`;

const StyledFormItem = styled(Form.Item)`
  .ant-form-item-label {
    padding-bottom: 8px;

    label {
      font-size: 14px;
      font-weight: 500;
      color: #262626;
      height: auto;

      &::after {
        content: none;
      }
    }
  }

  .ant-form-item-control-input {
    min-height: 48px;
  }
`;

const RegisterEmployee = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  const handleSubmit = async (values) => {
    if (fileList.length === 0) {
      message.error('Please upload at least 1 photo');
      return;
    }

    if (fileList.length > 20) {
      message.error('Maximum 20 photos allowed');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('emp_id', values.emp_id);
      formData.append('full_name', values.full_name);
      if (values.email) formData.append('email', values.email);
      if (values.department) formData.append('department', values.department);
      if (values.phone) formData.append('phone', values.phone);
      if (values.team) formData.append('team', values.team);
      if (values.division) formData.append('division', values.division);

      // Append all images
      fileList.forEach((file) => {
        formData.append('images', file.originFileObj);
      });

      await api.registerNewEmployee(formData);
      message.success(`Employee ${values.emp_id} registered successfully!`);
      form.resetFields();
      setFileList([]);
      navigate('/employees');
    } catch (error) {
      message.error(error.message || 'Failed to register employee');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadChange = ({ fileList: newFileList }) => {
    // Limit to 20 files
    if (newFileList.length > 20) {
      message.warning('Maximum 20 photos allowed');
      return;
    }
    setFileList(newFileList);
  };

  const handleRemoveImage = (file) => {
    setFileList(fileList.filter((item) => item.uid !== file.uid));
  };

  const handleAddPhoto = () => {
    if (fileList.length >= 20) {
      message.warning('Maximum 20 photos allowed');
      return;
    }
    // Trigger the file input
    document.getElementById('photo-upload-input').click();
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);

    files.forEach((file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error(`${file.name} is not an image file!`);
        return;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error(`${file.name} must be smaller than 5MB!`);
        return;
      }

      if (fileList.length < 20) {
        const newFile = {
          uid: `${Date.now()}-${Math.random()}`,
          name: file.name,
          status: 'done',
          originFileObj: file,
        };
        setFileList(prev => [...prev, newFile]);
      }
    });

    // Reset input value so same file can be selected again
    e.target.value = '';
  };

  const uploadProps = {
    accept: 'image/*',
    multiple: true,
    fileList: fileList,
    onChange: handleUploadChange,
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files!');
        return Upload.LIST_IGNORE;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('Image must be smaller than 5MB!');
        return Upload.LIST_IGNORE;
      }
      return false; // Prevent auto upload
    },
    onRemove: handleRemoveImage,
    showUploadList: false,
  };

  // Generate 20 slots for photos
  const photoSlots = Array.from({ length: 20 }, (_, index) => {
    const file = fileList[index];
    return { index, file };
  });

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ margin: 0 }}>
          <UserAddOutlined style={{ marginRight: 12 }} />
          Register New Employee
        </Title>
        <Button onClick={() => navigate('/employees')}>
          Back to Employees
        </Button>
      </div>

      <StyledCard>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ active: 'Y' }}
        >
          <Title level={4}>Employee Information</Title>
          <Divider />

          <Row gutter={16}>
            <Col span={12}>
              <StyledFormItem
                name="emp_id"
                label="Employee ID"
                rules={[
                  { required: true, message: 'Please enter employee ID' },
                  { min: 3, message: 'Employee ID must be at least 3 characters' },
                  { pattern: /^[A-Za-z0-9_-]+$/, message: 'Only letters, numbers, - and _ allowed' }
                ]}
                tooltip="Unique identifier for the employee (e.g., AVA0001)"
              >
                <StyledInput
                  placeholder="e.g., AVA0001"
                />
              </StyledFormItem>
            </Col>

            <Col span={12}>
              <StyledFormItem
                name="full_name"
                label="Full Name"
                rules={[{ required: true, message: 'Please enter full name' }]}
              >
                <StyledInput
                  placeholder="e.g., John Doe"
                  
                />
              </StyledFormItem>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <StyledFormItem
                name="email"
                label="Email Address"
                rules={[
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <StyledInput
                  placeholder="e.g., john.doe@company.com"
                  
                  type="email"
                />
              </StyledFormItem>
            </Col>

            <Col span={12}>
              <StyledFormItem
                name="phone"
                label="Phone Number"
              >
                <StyledInput
                  placeholder="e.g., +1234567890"
                  
                />
              </StyledFormItem>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <StyledFormItem
                name="department"
                label="Department"
              >
                <StyledInput
                  placeholder="e.g., Engineering"
                  
                />
              </StyledFormItem>
            </Col>

            <Col span={8}>
              <StyledFormItem
                name="division"
                label="Division"
              >
                <StyledInput
                  placeholder="e.g., Software Development"
                  
                />
              </StyledFormItem>
            </Col>

            <Col span={8}>
              <StyledFormItem
                name="team"
                label="Team"
              >
                <StyledInput
                  placeholder="e.g., Backend Team"
                  
                />
              </StyledFormItem>
            </Col>
          </Row>

          <Title level={4} style={{ marginTop: 32 }}>
            <CameraOutlined style={{ marginRight: 8 }} />
            Employee Photos
          </Title>
          <Text type="secondary">
            Upload 1-20 photos of the employee from different angles for better face recognition.
          </Text>
          <Divider />

          {/* Hidden file input */}
          <input
            id="photo-upload-input"
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
          />

          <div style={{ marginBottom: 16 }}>
            <Upload {...uploadProps}>
              <Button
                icon={<UploadOutlined />}
                disabled={fileList.length >= 20}
              >
                Select Photos ({fileList.length}/20)
              </Button>
            </Upload>
          </div>

          <ImagePreviewGrid>
            {photoSlots.map(({ index, file }) => (
              <ImagePreviewCard key={index} isEmpty={!file}>
                {file ? (
                  <>
                    <img
                      src={URL.createObjectURL(file.originFileObj)}
                      alt={file.name}
                    />
                    <DeleteOverlay
                      className="delete-overlay"
                      onClick={() => handleRemoveImage(file)}
                    >
                      <DeleteOutlined style={{ fontSize: 20 }} />
                    </DeleteOverlay>
                  </>
                ) : (
                  <AddPhotoPlaceholder onClick={handleAddPhoto}>
                    <PlusOutlined />
                    <span>Add Photo</span>
                  </AddPhotoPlaceholder>
                )}
              </ImagePreviewCard>
            ))}
          </ImagePreviewGrid>

          <Divider />

          <StyledFormItem style={{ marginTop: 32, marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button  onClick={() => navigate('/employees')}>
                Cancel
              </Button>
              <Button
                type="primary"
                
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={loading}
              >
                Register Employee
              </Button>
            </Space>
          </StyledFormItem>
        </Form>
      </StyledCard>
    </div>
  );
};

export default RegisterEmployee;
